import { createSlice } from "@reduxjs/toolkit";
import {
  getUnits,
  addNewUnit,
  updateUnit,
  deleteUnit,
  getUnitById,
  getUnitsByBlock,
} from "./thunk";
import {getContracts} from "../contracts/thunk";

export const initialState = {
  units: [],
  count: 0,
  totalPages: 1,
  currentPage: 1,
  pageSize: 20,
  nextPage: null,
  previousPage: null,
  error: null,
  selectedUnit: null,
  isUnitSuccess: false,
  isUnitCreated: false,
  loading: false
};

const unwrapList = (payload) => {
  if (payload && Array.isArray(payload.results)) {
    const p = payload.pagination || {};
    const count = p.count ?? payload.results.length;
    return {
      results: payload.results,
      count,
      totalPages: p.total_pages ?? 1,
      currentPage: p.current_page ?? 1,
      pageSize: p.page_size ?? payload.results.length,
      nextPage: p.next_page ?? null,
      previousPage: p.previous_page ?? null,
    };
  }
  const arr = Array.isArray(payload) ? payload : [];
  return {
    results: arr,
    count: arr.length,
    totalPages: 1,
    currentPage: 1,
    pageSize: arr.length,
    nextPage: null,
    previousPage: null,
  };
};

const unitsSlice = createSlice({
  name: "UnitsSlice",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch units
    builder.addCase(getUnits.fulfilled, (state, action) => {
      const meta = unwrapList(action.payload);
      state.units = meta.results;
      state.count = meta.count;
      state.totalPages = meta.totalPages;
      state.currentPage = meta.currentPage;
      state.pageSize = meta.pageSize;
      state.nextPage = meta.nextPage;
      state.previousPage = meta.previousPage;
      state.isUnitSuccess = true;
      state.error = null;
      state.loading = false;
    });

    builder.addCase(getUnits.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to fetch units";
      state.isUnitSuccess = false;
      state.loading = false;
    });
    builder.addCase(getUnits.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    // Fetch a single unit by ID
    builder.addCase(getUnitById.fulfilled, (state, action) => {
      state.selectedUnit = action.payload; // Set the selected unit
      state.error = null;
    });

    builder.addCase(getUnitById.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to fetch unit by ID";
      state.selectedUnit = null; // Clear the selected unit on failure
    });

    // Add new unit
    builder.addCase(addNewUnit.fulfilled, (state, action) => {
      state.units.push(action.payload); // Add the new unit to the list
      state.isUnitCreated = true;
      state.error = null;
    });

    builder.addCase(addNewUnit.rejected, (state, action) => {
      state.error = action.payload || "Failed to add unit";
      state.isUnitCreated = false;
    });

    // Update unit
    builder.addCase(updateUnit.fulfilled, (state, action) => {
      state.units = state.units.map((unit) =>
        unit.id === action.payload.id ? { ...unit, ...action.payload } : unit
      );
      state.error = null;
    });


    builder.addCase(updateUnit.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to update unit";
    });

    builder.addCase(getUnitsByBlock.fulfilled, (state, action) => {
      const meta = unwrapList(action.payload);
      state.units = meta.results;
      state.count = meta.count;
      state.totalPages = meta.totalPages;
      state.currentPage = meta.currentPage;
      state.pageSize = meta.pageSize;
      state.nextPage = meta.nextPage;
      state.previousPage = meta.previousPage;
      state.isUnitSuccess = true;
      state.error = null;
    });

    builder.addCase(getUnitsByBlock.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to fetch units by block";
      state.isUnitSuccess = false;
    });

    // Delete unit
    builder.addCase(deleteUnit.fulfilled, (state, action) => {
      state.units = state.units.filter(
        (unit) => unit.id !== action.payload.unitId
      );
      state.error = null;
    });

    builder.addCase(deleteUnit.rejected, (state, action) => {
      state.error = action.payload?.error || "Failed to delete unit";
    });
  },
});

export default unitsSlice.reducer;
